import { useTranslation } from "react-i18next";
import { useFieldContext } from "../hooks/surveyResponseFormHooks";
import { QuestionType, type Question } from "@pulse/api/surveyApi";

interface MultipleChoiceAnswerOptionsProps {
    question: Question;
}

function MultipleChoiceAnswerOptions({ question }: MultipleChoiceAnswerOptionsProps) {
    const field = useFieldContext();
    const { t } = useTranslation();
    const isMultiSelect = question.questionType === QuestionType.MULTI_SELECT;
    const selected = Array.isArray(field.state.value) ? (field.state.value as number[]) : [];

    if (!question.questionOptions || question.questionOptions.length === 0) {
        return (
            <div className="mb-8">
                <p className="text-gray-500 italic">{t("answers.noOptions")}</p>
            </div>
        );
    }

    // Unselecting the last option clears the answer entirely — an empty array would
    // otherwise count as answered in the status bar and on submit.
    const toggle = (index: number) => {
        const next = selected.includes(index)
            ? selected.filter((i) => i !== index)
            : [...selected, index].sort((a, b) => a - b);

        field.handleChange(next.length > 0 ? next : undefined);
    };

    return (
        <div
            className="mb-8"
            role={isMultiSelect ? "group" : "radiogroup"}
            aria-label={t(isMultiSelect ? "answers.multiSelectOptions" : "answers.choiceOptions")}
        >
            {question.questionOptions.map((option, index) => {
                if (!option.trim()) return null; // Skip empty options

                const isSelected = isMultiSelect
                    ? selected.includes(index)
                    : field.state.value === index;

                return (
                    <label
                        key={index}
                        className={`mb-3 flex cursor-pointer items-center rounded-[3px] border p-3 transition-colors ${
                            isSelected
                                ? "border-chameleon-green bg-chameleon-green-10"
                                : "border-compendia-border hover:bg-chameleon-green-10/50"
                        }`}
                    >
                        <input
                            type={isMultiSelect ? "checkbox" : "radio"}
                            value={index}
                            checked={isSelected}
                            onChange={() =>
                                isMultiSelect ? toggle(index) : field.handleChange(index)
                            }
                            className="mr-3 h-4 w-4 accent-chameleon-green text-chameleon-green focus:ring-chameleon-green"
                            name={field.name}
                        />
                        <span className="text-sm">{option}</span>
                    </label>
                );
            })}
        </div>
    );
}

export default MultipleChoiceAnswerOptions;
